/**
 * Service for interacting with appointment cache via backend API
 */

class AppointmentCacheService {
  /**
   * Get auth token from localStorage
   */
  getAuthToken() {
    return localStorage.getItem('token');
  }

  /**
   * Get cached appointments within a date range
   */
  async getCachedAppointments(startDate, endDate) {
    try {
      const token = this.getAuthToken();
      const response = await fetch('/api/appointments-cache/appointments', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }

      // Filter by date range if provided
      if (startDate && endDate) {
        return result.data.filter(apt => {
          const aptDate = apt.date;
          return aptDate >= startDate && aptDate <= endDate;
        });
      }

      return result.data;
    } catch (error) {
      console.error('Error in getCachedAppointments:', error);
      return [];
    }
  }

  /**
   * Get all cached appointments (for dashboard stats)
   * @param {Object} options - Optional date filtering
   * @param {string} options.start_date - Start date in MM/DD/YYYY format
   * @param {string} options.end_date - End date in MM/DD/YYYY format
   */
  async getAllCachedAppointments(options = {}) {
    try {
      const token = this.getAuthToken();
      
      // Build query params if dates provided
      const params = new URLSearchParams();
      if (options.start_date) params.append('start_date', options.start_date);
      if (options.end_date) params.append('end_date', options.end_date);
      
      const url = `/api/appointments-cache/appointments${params.toString() ? '?' + params.toString() : ''}`;
      console.log('[Frontend Cache] Fetching appointments from:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error);
      }

      console.log('[Frontend Cache] Received appointments:', result.count);
      return result.data;
    } catch (error) {
      console.error('[Frontend Cache] Error in getAllCachedAppointments:', error);
      return [];
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    try {
      console.log('[Frontend Cache] Fetching cache stats...');
      const token = this.getAuthToken();
      const response = await fetch('/api/appointments-cache/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('[Frontend Cache] Response status:', response.status);
      const result = await response.json();
      console.log('[Frontend Cache] Response data:', result);
      
      if (!result.success) {
        throw new Error(result.error);
      }

      const stats = {
        total_cached: result.data.total_appointments || 0,
        earliest_date: result.data.earliest_date,
        latest_date: result.data.latest_date,
        total_patients: result.data.total_patients || 0,
        total_providers: result.data.total_providers || 0,
        appointments_today: result.data.appointments_today || 0,
        new_patients_30d: result.data.new_patients_30d || 0,
        last_sync: result.data.last_sync_time
      };
      
      console.log('[Frontend Cache] Parsed stats:', stats);
      return stats;
    } catch (error) {
      console.error('[Frontend Cache] Error in getCacheStats:', error);
      return {
        total_cached: 0,
        earliest_date: null,
        latest_date: null,
        total_patients: 0,
        total_providers: 0,
        appointments_today: 0,
        new_patients_30d: 0,
        last_sync: null
      };
    }
  }

  /**
   * Get last sync status
   */
  async getLastSyncStatus() {
    try {
      console.log('[Frontend Cache] Fetching sync status...');
      const token = this.getAuthToken();
      const response = await fetch('/api/appointments-cache/sync-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('[Frontend Cache] Sync status response:', response.status);
      const result = await response.json();
      console.log('[Frontend Cache] Sync status data:', result);
      
      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data;
    } catch (error) {
      console.error('[Frontend Cache] Error in getLastSyncStatus:', error);
      return null;
    }
  }

  /**
   * Check if cache is ready (has data)
   */
  async isCacheReady() {
    try {
      console.log('[Frontend Cache] Checking if cache is ready...');
      const stats = await this.getCacheStats();
      const lastSync = await this.getLastSyncStatus();

      // Cache is ready if we have data, regardless of sync log status
      const hasData = stats.total_cached > 0;
      const isReady = hasData;

      console.log('[Frontend Cache] Cache ready check:', {
        hasData,
        total_cached: stats.total_cached,
        lastSyncStatus: lastSync?.status,
        ready: isReady
      });

      return {
        ready: isReady,
        stats,
        lastSync
      };
    } catch (error) {
      console.error('[Frontend Cache] Error checking cache readiness:', error);
      return {
        ready: false,
        stats: null,
        lastSync: null
      };
    }
  }

  /**
   * Get pre-computed yearly chart data (12 monthly counts)
   */
  async getYearlyChartData() {
    try {
      console.log('[Frontend Cache] Fetching yearly chart data...');
      const token = this.getAuthToken();
      const response = await fetch('/api/appointments-cache/chart/yearly', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      
      if (!result.success) throw new Error(result.error);
      
      console.log('[Frontend Cache] Yearly chart data:', result.data);
      return result.data;
    } catch (error) {
      console.error('[Frontend Cache] Error in getYearlyChartData:', error);
      return [];
    }
  }

  /**
   * Get pre-computed monthly chart data (5 weekly counts)
   * @param {number} year - Target year
   * @param {number} month - Target month (1-12)
   */
  async getMonthlyChartData(year, month) {
    try {
      console.log(`[Frontend Cache] Fetching monthly chart data for ${year}-${month}...`);
      const token = this.getAuthToken();
      const params = new URLSearchParams({ year, month });
      const response = await fetch(`/api/appointments-cache/chart/monthly?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      
      if (!result.success) throw new Error(result.error);
      
      console.log('[Frontend Cache] Monthly chart data:', result.data);
      return result.data;
    } catch (error) {
      console.error('[Frontend Cache] Error in getMonthlyChartData:', error);
      return [];
    }
  }

  /**
   * Get pre-computed weekly chart data (7 daily counts)
   * @param {string} weekStart - Week start date in YYYY-MM-DD format
   */
  async getWeeklyChartData(weekStart) {
    try {
      console.log(`[Frontend Cache] Fetching weekly chart data for week starting ${weekStart}...`);
      const token = this.getAuthToken();
      const params = new URLSearchParams({ week_start: weekStart });
      const response = await fetch(`/api/appointments-cache/chart/weekly?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      
      if (!result.success) throw new Error(result.error);
      
      console.log('[Frontend Cache] Weekly chart data:', result.data);
      return result.data;
    } catch (error) {
      console.error('[Frontend Cache] Error in getWeeklyChartData:', error);
      return [];
    }
  }
}

export default new AppointmentCacheService();
